import classNames from 'classnames';
import _ from 'lodash';
import type { Parchment } from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import IconButton from '../../components/IconButton';
import SVGIcon from '../../components/SVGIcon';
import { IS_EXTENSION } from '../../constants/platform';
import caretIcon from '../../icons/non-prefilled/16x16/caret.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import { useQuill } from '../QuillContext';
import { getHeaderCollapse } from '../useHeaderCollapse';
import useShowEditorToast from '../utils/useShowEditorToast';
import BasicHeader from './BasicHeader';
import styles from './Header.module.scss';
import Scroll from './Scroll';
import { usePostLayoutSize } from '../../utils/responsive/PostLayoutSizeProvider';

interface HeaderButtonsProps {
  blot: Header;
  copyLinkDisabled: boolean;
}

const HeaderButtons = ({ blot, copyLinkDisabled }: HeaderButtonsProps) => {
  const { quill } = useQuill();
  const ref = useRef<HTMLDivElement | null>(null);
  const [lineHeight, setLineHeight] = useState<string | undefined>(undefined);
  const postLayoutSize = usePostLayoutSize();

  const shouldShowCopyLinkButton =
    !copyLinkDisabled && !['mobile', 'tablet'].includes(postLayoutSize);

  const shouldShowHeaderButtons = postLayoutSize !== 'mobile';

  useEffect(() => {
    if (ref.current) {
      setLineHeight(getComputedStyle(ref.current).lineHeight);
    }
  }, []);

  useEffect(() => {
    if (!quill) return;
    getHeaderCollapse(quill)?.setIsCollapsible(shouldShowHeaderButtons);
  }, [quill, shouldShowHeaderButtons]);

  const showEditorToast = useShowEditorToast();

  if (!shouldShowHeaderButtons) return null;

  return (
    <div
      ref={ref}
      className={styles.buttonContainer}
      style={{ height: lineHeight }}
    >
      {shouldShowCopyLinkButton && (
        <IconButton
          className={classNames(styles.button, styles.linkButton)}
          onClick={async () => {
            const { location } = window;
            const headerAnchorLink = new URL(location.href);
            headerAnchorLink.hash = blot.getAnchorId();

            await navigator.clipboard.writeText(headerAnchorLink.toString());
            showEditorToast(
              { level: 'success', message: 'Link copied to clipboard' },
              2000,
            );
          }}
          testId="headerLinkButton"
          aria-label="Copy link to header"
        >
          {linkIcon}
        </IconButton>
      )}
      <IconButton
        className={classNames(styles.button, styles.collapseButton)}
        onClick={() => {
          if (quill) {
            getHeaderCollapse(quill)?.setHeaderCollapsed(blot);
          }
        }}
        testId="headerCollapseButton"
        aria-label="Toggle header collapse"
      >
        <SVGIcon className={styles.collapseButtonIcon}>{caretIcon}</SVGIcon>
      </IconButton>
    </div>
  );
};

export default class Header extends BasicHeader {
  id: string;
  declare domNode: HTMLHeadingElement;
  protected copyLinkDisabled = false;
  protected override anchorIdEnabled = true;

  constructor(scroll: Parchment.ScrollBlot, domNode: Node) {
    super(scroll, domNode);
    if (domNode instanceof HTMLElement) {
      domNode.classList.add(styles.header);
    }
    this.id = _.uniqueId();
    const uiNode = document.createElement('div');
    uiNode.dataset.noPrint = '';
    this.attachUI(uiNode);
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  renderPortal() {
    return (
      this.uiNode &&
      createPortal(
        <HeaderButtons
          blot={this}
          copyLinkDisabled={IS_EXTENSION || this.copyLinkDisabled}
        />,
        this.uiNode,
      )
    );
  }
}
